import { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useAmplitude } from '@crehana/analytics';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { colors, Loader, PrimaryButton } from '@crehana/ui';
import { useMe } from '@crehana/web/ReactContexts/AuthContext';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { useForm } from 'react-hook-form';

import SEND_B2B_ORGANIZATION_PAYMENT from '@/views/B2BCheckout/graphql/SendB2BOrganizationPayment.v4.b2b.graphql';
import type {
  SendB2BOrganizationPayment,
  SendB2BOrganizationPaymentVariables,
} from '@/views/B2BCheckout/graphql/types/SendB2BOrganizationPayment';
import useCheckoutCtx from '@/views/B2BCheckout/hooks/useCheckoutCtx';
import useCheckoutTranslation from '@/views/B2BCheckout/hooks/useCheckoutTranslations';
import useStripe from '@/views/B2BCheckout/hooks/useStripe';

import type { TKeys } from '../../../../i18n';
import {
  BASE_INPUT_CONTAINER_CLASS,
  BASE_INPUT_STRIPE_CONTAINER,
  STRIPE_INPUT,
  stripeFields,
} from './constants';

const { baseMain, grayDark, redMain } = colors;

const PaymentForm = () => {
  const { stripe } = useStripe();

  const { me } = useMe();
  const router = useRouter();
  const [errorPayment, setErrorPayment] = useState('');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const { logEvent } = useAmplitude();

  const {
    state: { accountDetails, licences, totalPrice },
    currency,
    productName,
    productId,
  } = useCheckoutCtx();

  const { t } = useCheckoutTranslation();

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onChange',
  });

  const [sendB2BOrganizationPayment, { loading }] = useMutation<
    SendB2BOrganizationPayment,
    SendB2BOrganizationPaymentVariables
  >(SEND_B2B_ORGANIZATION_PAYMENT, {
    context: { clientName: 'v4.b2b' },
  });

  // DO NOT REMOVE USEMEMO. ON ANY RE RENDER, STRIPE INSTANCE WILL BE LOST
  const elements = useMemo(
    () =>
      stripe?.elements({
        fonts: [
          {
            cssSrc:
              'https://fonts.googleapis.com/css2?family=Nunito&display=swap',
          },
        ],
      }),
    [stripe],
  );

  const onSubmit = async () => {
    setErrorPayment('');
    setPaymentProcessing(true);
    const cardElement = elements?.getElement('cardNumber');

    if (stripe && cardElement && accountDetails) {
      const {
        firstName,
        lastName,
        email,
        phoneNumber: { code, phoneNumber },
      } = accountDetails;

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: firstName + lastName,
          email,
          phone: code + phoneNumber,
        },
      });

      if (error || !paymentMethod) {
        // Inform the customer that there was an error.
        const errorElement = document.getElementById('card-errors');

        if (errorElement?.textContent && error?.message) {
          errorElement.textContent = error.message;
        }

        setErrorPayment(error?.message || '');
        setPaymentProcessing(false);
      } else {
        // Send the token to your server.
        try {
          const { data } = await sendB2BOrganizationPayment({
            variables: {
              email: accountDetails.email,
              pmToken: paymentMethod.id,
              userId: me!.originalId!,
              seats: licences,
              opportunityId: accountDetails.opportunityId!,
              productId,
              currency,
              method: 1,
              amount: totalPrice,
              itemDescription: productName,
            },
          });

          if (data?.sendPayData?.success) {
            logEvent({
              event: 'Submit B2B Payment Form Success',
              data: {},
              callback: () => {
                router.push(
                  `/checkout/b2b/thanks/${accountDetails.opportunityId}/`,
                );
              },
            });
          } else {
            logEvent({
              event: 'Submit B2B Payment Form Error',
              data: {},
              callback: () => {
                const translationKey =
                  (data?.sendPayData?.paymentStatus?.translationKey as TKeys) ||
                  'CATCH_MESSAGE';
                const errorMessageTranslation = t(translationKey);

                setErrorPayment(
                  errorMessageTranslation === translationKey
                    ? t('CATCH_MESSAGE')
                    : errorMessageTranslation,
                );
              },
            });

            setPaymentProcessing(false);
          }
        } catch (err) {
          logEvent({
            event: 'Submit B2B Payment Form Success',
            data: {},
            callback: () => {
              setErrorPayment('Oops, ocurrió un error');
              setPaymentProcessing(false);
              console.log(err);
            },
          });
        }
      }
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    logEvent({
      event: 'Render B2B Payment Form',
      data: {},
    });
  }, []);

  useEffect(() => {
    if (elements) {
      const elementsArray = stripeFields.map(
        ({ field, id, placeholder, extraFields }) => {
          const cardElement = elements.create(field, {
            ...extraFields,
            placeholder,
            style: {
              base: {
                color: baseMain,
                fontWeight: '400',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '16px',
                '::placeholder': {
                  color: grayDark,
                },
              },
              invalid: {
                color: redMain,
              },
            },
          });

          return { field, cardElement, id };
        },
      );

      // biome-ignore lint/style/useBlockStatements: legacy code - style/useBlockStatements
      if (elementsArray.length > 0)
        elementsArray.forEach(({ cardElement, id }) => {
          cardElement.mount(id);
        });

      return () => {
        elementsArray.forEach(({ cardElement }) => {
          cardElement.destroy();
        });
      };
    }
  }, [elements]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-wrap w-full">
      <div className="flex flex-wrap w-full">
        {stripeFields.map(({ id, label, field }, idx) => (
          <div
            key={id}
            className={clsx(
              BASE_INPUT_STRIPE_CONTAINER,
              idx % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12',
            )}
          >
            <label
              htmlFor={field}
              className="font-subtitle3 mb-8 text-base-main"
            >
              {label}
            </label>
            <div id={field} className={STRIPE_INPUT} />
          </div>
        ))}
      </div>
      <div className={clsx(BASE_INPUT_CONTAINER_CLASS, 'lg:pr-12')}>
        {loading ? (
          <div className="flex justify-center items-center">
            <Loader size={24} />
          </div>
        ) : (
          <PrimaryButton
            type="submit"
            label="Comprar ahora"
            isAccent
            full
            disabled={isSubmitting || paymentProcessing}
          />
        )}

        {errorPayment && (
          <p className="font-subtitle4 text-red-main text-center mt-12">
            {errorPayment}
          </p>
        )}
      </div>
    </form>
  );
};

export default PaymentForm;
